import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Mail, Phone, MapPin, Linkedin, Facebook, Instagram, Cookie } from 'lucide-react';
import { useCookieConsent } from './CookieConsentProvider';

const LOGO_FULL = "https://equiprwebsitestorage.blob.core.windows.net/website-assets/EQUIPR_slogan_Rev-01.png";
const CICA_LOGO = "https://equiprwebsitestorage.blob.core.windows.net/website-assets/logos/CICA_Member_Logo.jpg";

const Footer = () => {
  const location = useLocation();
  const isCranePage = location.pathname === '/mobile-crane-hire-rigging';
  const { openPreferences } = useCookieConsent() || {};

  return (
    <footer className="bg-equipr-dark border-t border-white/5" data-testid="main-footer">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 md:gap-8 items-start">
          {/* Brand Column */}
          <div className="lg:col-span-1">
            <Link to="/" className="inline-block mb-6">
              <img src={LOGO_FULL} alt="EQUIPR Software - Plan Better. Work Better." className="h-14" />
            </Link>
            <p className="text-white/60 text-sm leading-relaxed">
              Streamline your operations with centralised management software for your plant, equipment, operators, and compliance.
            </p>
          </div>

          {/* Plan Better */}
          <div>
            <h4 className="font-heading text-white uppercase tracking-widest text-sm mb-6">Plan Better</h4>
            <ul className="space-y-3">
              <li>
                <Link to="/allocations-scheduling-operated-plant-hire" className="text-white/60 hover:text-equipr-yellow text-sm transition-colors" data-testid="footer-link-allocations">
                  Allocations & Scheduling
                </Link>
              </li>
              <li>
                <Link to="/operator-mobile-dockets-timesheets" className="text-white/60 hover:text-equipr-yellow text-sm transition-colors" data-testid="footer-link-dockets">
                  Mobile Dockets & Timesheets
                </Link>
              </li>
              <li>
                <Link to="/servicing-and-maintenance-software" className="text-white/60 hover:text-equipr-yellow text-sm transition-colors" data-testid="footer-link-servicing">
                  Servicing & Maintenance
                </Link>
              </li>
            </ul>
          </div>

          {/* Work Better */}
          <div>
            <h4 className="font-heading text-white uppercase tracking-widest text-sm mb-6">Work Better</h4>
            <ul className="space-y-3">
              <li>
                <Link to="/plant-hire-software" className="text-white/60 hover:text-equipr-yellow text-sm transition-colors" data-testid="footer-link-plant-hire">
                  Operated Plant Hire
                </Link>
              </li>
              <li>
                <Link to="/civil-construction-operations-software" className="text-white/60 hover:text-equipr-yellow text-sm transition-colors" data-testid="footer-link-civil">
                  Civil Construction
                </Link>
              </li>
              <li>
                <Link to="/mobile-crane-hire-rigging" className="text-white/60 hover:text-equipr-yellow text-sm transition-colors" data-testid="footer-link-cranes">
                  Mobile Cranes & Rigging
                </Link>
              </li>
              <li>
                <Link to="/truck-cartage-software" className="text-white/60 hover:text-equipr-yellow text-sm transition-colors" data-testid="footer-link-truck-cartage">
                  Truck / Cartage
                </Link>
              </li>
              <li>
                <Link to="/concrete-pumping-software" className="text-white/60 hover:text-equipr-yellow text-sm transition-colors" data-testid="footer-link-concrete-pumping">
                  Concrete Pumping
                </Link>
              </li>
              <li>
                <Link to="/road-sweeping-software" className="text-white/60 hover:text-equipr-yellow text-sm transition-colors" data-testid="footer-link-road-sweeping">
                  Road Sweeping
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-heading text-white uppercase tracking-widest text-sm mb-6">Contact Us</h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <Phone className="h-4 w-4 text-equipr-yellow mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-white/60 text-sm">AU: 1300 100 365</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <Mail className="h-4 w-4 text-equipr-yellow mt-0.5 flex-shrink-0" />
                <div>
                  <a href="mailto:info@equipr.software" className="text-white/60 hover:text-equipr-yellow text-sm transition-colors block">info@equipr.software</a>
                  <a href="mailto:info.eu@equipr.software" className="text-white/60 hover:text-equipr-yellow text-sm transition-colors block">info.eu@equipr.software</a>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <MapPin className="h-4 w-4 text-equipr-yellow mt-0.5 flex-shrink-0" />
                <p className="text-white/60 text-sm">
                  UniSC – Innovation Center<br />
                  90 Sippy Downs Drive<br />
                  QLD 4556, Australia
                </p>
              </li>
            </ul>
            
            {/* Social Media Links */}
            <div className="mt-6 pt-6 border-t border-white/10">
              <p className="text-white/40 text-xs uppercase tracking-wider mb-4">Follow Us</p>
              <div className="flex gap-4">
                <a 
                  href="https://www.linkedin.com/company/13664129" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-white/5 hover:bg-equipr-yellow/20 border border-white/10 hover:border-equipr-yellow/50 rounded-lg flex items-center justify-center transition-all group"
                  data-testid="social-linkedin"
                >
                  <Linkedin className="h-4 w-4 text-white/60 group-hover:text-equipr-yellow transition-colors" />
                </a>
                <a 
                  href="https://www.facebook.com/profile.php?id=100076819572027" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-white/5 hover:bg-equipr-yellow/20 border border-white/10 hover:border-equipr-yellow/50 rounded-lg flex items-center justify-center transition-all group"
                  data-testid="social-facebook"
                >
                  <Facebook className="h-4 w-4 text-white/60 group-hover:text-equipr-yellow transition-colors" />
                </a>
                <a 
                  href="https://www.instagram.com/equipr_software/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-white/5 hover:bg-equipr-yellow/20 border border-white/10 hover:border-equipr-yellow/50 rounded-lg flex items-center justify-center transition-all group"
                  data-testid="social-instagram"
                >
                  <Instagram className="h-4 w-4 text-white/60 group-hover:text-equipr-yellow transition-colors" />
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-16 pt-8 border-t border-white/5 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-white/40 text-sm">
            © {new Date().getFullYear()} EQUIPR Software Pty Ltd. All rights reserved.
          </p>
          <div className="flex items-center gap-6 flex-wrap justify-center">
            <Link to="/legal/terms" className="text-white/40 hover:text-white/60 text-sm transition-colors" data-testid="footer-link-terms">
              Terms of Service
            </Link>
            <Link to="/legal/privacy" className="text-white/40 hover:text-white/60 text-sm transition-colors" data-testid="footer-link-privacy">
              Privacy Policy
            </Link>
            <Link to="/trust/security" className="text-white/40 hover:text-white/60 text-sm transition-colors" data-testid="footer-link-security">
              Security
            </Link>
            <Link to="/legal" className="text-white/40 hover:text-white/60 text-sm transition-colors" data-testid="footer-link-legal">
              Legal & Trust
            </Link>
            {openPreferences && (
              <button
                onClick={openPreferences}
                className="text-white/40 hover:text-white/60 text-sm transition-colors flex items-center gap-1"
                data-testid="footer-cookie-preferences"
              >
                <Cookie className="h-3.5 w-3.5" />
                Cookie Preferences
              </button>
            )}
            {isCranePage && (
              <a href="https://www.cica.com.au" target="_blank" rel="noopener noreferrer" data-testid="cica-logo-link">
                <img
                  src={CICA_LOGO}
                  alt="Proud Member — CICA — The Crane Industry Council of Australia"
                  className="h-16 w-auto rounded"
                  data-testid="cica-member-logo"
                />
              </a>
            )}
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
